/* oxlint-disable */
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';

import {
  dayStart, tzDate,
} from '@formkit/tempo';
import Crypto from 'crypto';
import jwt from 'jsonwebtoken';
import moment from 'moment';

import {
  BetStatus, CalendarRange, ErrorCodes, GameResult, Gateway, TransactionStates, TransactionTypes,
  VoucherTypes,
} from '@/enums/core';
import { core } from '@/localization';
import { errors } from '@/localization/errors';

import { Encrypt } from '@/interfaces/core';

import {
  LOCAL, ALGORITHM, ROWS_PER_PAGE, JWT_JOIN,
} from '@/constants/core';

// =============== FORMAT DATE
export const formatDate = (date: Date, short: boolean = false): string | Date => {
  const timeZone = 'America/El_Salvador';
  return short ? tzDate(dayStart(date), timeZone).toISOString().slice(0, 10) : tzDate(dayStart(date), timeZone);
};

export const startAndEndDay = (): { start: string, end: string } => {
  const start = moment().set('hour', 6).set('minute', 0).set('second', 0)
    .format('YYYY-MM-DD HH:mm:ss');
  const end = moment().add(1, 'days').set('hour', 6).set('minute', 0)
    .set('second', 0)
    .format('YYYY-MM-DD HH:mm:ss');
  return { start, end };
};

const randomString = (length:number, string:boolean = false):string => {
  const chars = string ? 'abcdefghijklmnopqrstuvwxyz' : '0123456789abcdefghijklmnopqrstuvwxyz';
  let data = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    data += chars[randomIndex];
  }

  return data;
};

export const extractNumberOfFakeUuid = (value: string): number => {
  return parseInt(value.split('-')[0].replace(/[^0-9]/g, ''), 10);
};

export const fakeUuid = (value: string): string => {
  const data = `${value}${randomString(8 - value.length, true)}-${randomString(4)}-4${randomString(3)}-y${randomString(3)}-${randomString(12)}`;
  return data;
};

export const checkHmacGateway = async (data: any, privateKey:any): Promise<boolean> => {
  const paramsArray:Array<any> = Object.values(data);
  const paramsArrayCopy = [...paramsArray];
  paramsArrayCopy.pop();
  paramsArrayCopy.push(privateKey);
  const dataHash = paramsArrayCopy.join('^');
  const signature = Crypto.createHash('sha256').update(dataHash).digest('hex');
  return signature === data.h_signature;
};

export const checkHmacGatewayForm = async (data: any, privateKey:any): Promise<boolean> => {
  const hSignature = data.h_signature;
  delete data.h_signature;
  const paramsArray:Array<any> = Object.values(data);
  const dataJoin = paramsArray.join('^');
  const dataHash = `${dataJoin}^${privateKey}`;
  const signature = Crypto.createHash('sha256').update(dataHash).digest('hex');
  console.log({ signature, hSignature });
  return signature === hSignature;
};

export const checkGatewayJWT = (token: string): boolean => {
  const result = jwt.verify(token, process.env.JWT_SECRET_KEY_TO_GATEWAYS!);
  return !!result;
};

export const errorCodeMessage = (error:number): string => {
  const array = [
    { code: ErrorCodes.NO_AUTHORIZATION, message: 'No autorizado' },
    { code: ErrorCodes.NO_JWT_TOKEN, message: 'No JWT token' },
  ];
  const result = array.find((item) => { return item.code === error; });
  return result?.message || '';
};

export const checkAllowedIp = (allowedIps: string, ip: string):boolean => {
  return allowedIps.split(',').includes(ip);
};

export const checkAuthorization = (headers: Headers): { message: string, status: boolean, token?: string } => {
  const authorization = headers.get('authorization');

  if (!authorization) return { message: errorCodeMessage(ErrorCodes.NO_AUTHORIZATION), status: false };

  const jwtToken = authorization.split(' ')[1];

  if (!jwtToken) return { message: errorCodeMessage(ErrorCodes.NO_JWT_TOKEN), status: false };

  return { message: '', status: true, token: jwtToken };
};

export const setGatewayJWT = (token: string): string | boolean => {
  if (token !== '') {
    const date = moment().format('YYYY-MM-DD HH:mm:ss');
    const payload = {
      data: `${token}${JWT_JOIN}${date}`,
    };
    const options = {
      // expiresIn: '1d',
      expiresIn: process.env.JWT_EXPIRES_TIME,
    } as jwt.SignOptions;
    return jwt.sign(payload, process.env.JWT_SECRET_KEY_TO_GATEWAYS!, options);
  }
  return false;
};

// add or remove characters to the UID value
export const uidProcess = (type: number, value: string): string => {
  const characters = 50;
  let result = '';
  if (type === 1) {
    const start = Crypto.randomBytes(characters).toString('base64').slice(0, characters);
    const end = Crypto.randomBytes(characters).toString('base64').slice(0, characters);
    result = `${start}${value}${end}`;
  } else if (type === 2) result = value.slice(characters, -characters);
  return result;
};

export const calendarRange: Record<CalendarRange, string> = {
  [CalendarRange.TODAY]: core.today,
  [CalendarRange.YESTERDAY]: core.yesterday,
  [CalendarRange.THREE_DAYS_AGO]: core.threeDaysAgo,
  [CalendarRange.ONE_WEEK_AGO]: core.oneWeekAgo,
  [CalendarRange.LAST_30_DAYS]: core.last30Days,
};

export const betStatus = (value: string):string => {
  switch (value) {
    case BetStatus.LOST:
      return core.lost;
    case BetStatus.WON:
      return core.won;
    case BetStatus.CASHOUT:
      return core.cashout;
    case BetStatus.VOID:
      return core.canceladaVoid;
    case BetStatus.REJECTED:
      return core.canceladaRejected;
    case BetStatus.PENDING:
      return core.pending;
    case BetStatus.CASHOUT_PARCIAL:
      return core.cashoutParcial;
    case BetStatus.OPEN:
      return core.open;
    default:
      return '';
  }
};

export const transactionStatus = (value: number):string => {
  switch (value) {
    case TransactionStates.APPROVED:
      return core.transactionApproved;
    case TransactionStates.COMPLETE:
      return core.transactionCompleted;
    case TransactionStates.CANCELLED:
      return core.transactionCancelled;
    case TransactionStates.REJECTED:
      return core.transactionRejected;
    case TransactionStates.ISSUED:
      return core.transactionIssued;
    case TransactionStates.ROLLBACK:
      return core.transactionRollback;
    case TransactionStates.PENDING:
      return core.transactionPending;
    case TransactionStates.FINISHED:
      return core.transactionCancelled;
    case TransactionStates.IN_PROCESS:
      return core.transactionInProcess;
    default:
      return '';
  }
};

export const gameResult = (value: number): string => {
  switch (value) {
    case GameResult.BET:
      return core.lost;
    case GameResult.WIN:
      return core.won;
    case GameResult.ROLLBACK:
      return core.rollback;
    default:
      return '';
  }
};

export const getPageAndSkip = (pageParam: string | null): { page: number, pageSize: number, skip: number; } => {
  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const pageSize = ROWS_PER_PAGE;
  const skip = (page - 1) * pageSize;
  return { page, pageSize, skip };
};

export const exception = async (error: any, info: { route: string, method: string, req: any }): Promise<void> => {
  if (process.env.NEXT_PUBLIC_APP_ENV !== LOCAL && error !== null) {
    console.error('Exception occurred:', {
      error,
      route: info.route,
      method: info.method,
      request: info.req,
    });
  }
};

export const encrypt = async (data: string | number): Promise<Encrypt | string> => {
  try {
    const key = Buffer.from(process.env.NEXT_PUBLIC_CRYPTO_SECRET_KEY!, 'base64');
    const iv = Buffer.from(process.env.NEXT_PUBLIC_CRYPTO_SECRET_IV!, 'base64');
    const cipher = Crypto.createCipheriv(ALGORITHM, key, iv);

    let encryptData = cipher.update(typeof data !== 'string' ? data.toString() : data, 'utf8', 'base64');
    encryptData += cipher.final('base64');

    const tag = cipher.getAuthTag().toString('base64');

    return { data: encryptData, tag };
  } catch (error: any) {
    return errors.tryAgain;
  }
};

export const decrypt = async (data: string, tag: string): Promise<string | string> => {
  try {
    if (data && tag) {
      const key = Buffer.from(process.env.NEXT_PUBLIC_CRYPTO_SECRET_KEY!, 'base64');
      const iv = Buffer.from(process.env.NEXT_PUBLIC_CRYPTO_SECRET_IV!, 'base64');
      console.log('🚀 ~ decrypt ~ key:', key);
      const decipher = Crypto.createDecipheriv(ALGORITHM, key, iv);

      const authTag = Buffer.from(tag, 'base64');
      decipher.setAuthTag(authTag);
      const encryptedData = Buffer.from(data, 'base64');
      let decrypted = decipher.update(encryptedData);
      decrypted = Buffer.concat([decrypted, decipher.final()]);
      console.log('🚀 ~ decrypt ~ decrypted:', decrypted.toString('utf8'));
      return decrypted.toString('utf8');
    }
    return errors.tryAgain;
  } catch (error: any) {
    return errors.tryAgain;
  }
};

export function signToken(data: any, expiresIn: string) {
  const options = {
    expiresIn,
  } as jwt.SignOptions;
  const payload = {
    data,
  };
  return jwt.sign(payload, process.env.JWT_SECRET_KEY_TO_GATEWAYS!, options);
}

export function verifyToken(token: string) {
  try {
    return jwt.verify(token, process.env.JWT_SECRET_KEY_TO_GATEWAYS!);
  } catch (error) {
    return null;
  }
}

export function signOTPToken(data: any, expiresIn: string | number) {
  const SECRET = process.env.JWT_SECRET_EMAIL || 'your-secret';
  const options = {
    expiresIn,
  } as jwt.SignOptions;
  const payload = {
    data,
  };

  const token = jwt.sign(payload, SECRET, options);
  return token;
}

export function verifyOTPToken(token: string): any | null {
  try {
    if (!token) throw new Error('Missing token');
    const SECRET = process.env.JWT_SECRET_EMAIL || 'your-secret';
    const decoded = jwt.verify(token, SECRET);
    return decoded;
  } catch (err) {
    console.error('Token verification failed:', err);
    return null; // Ensure a value is returned in case of an error
  }
}

export const checkToken = async (data: string) => {
  try {
    const resToken = await fetch('/api/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data,
    });

    if (!resToken.ok) {
      return false;
    }

    const { data: token } = await resToken.json();

    return token;
  } catch (error) {
    return false;
  }
};

export const checkUserId = (userId: number): boolean => {
  if (userId && typeof userId === 'number' && userId !== undefined) return true;
  return false;
};

export const checkNumber = (value: number): boolean => {
  if (value && typeof value === 'number' && value !== undefined && !Number.isNaN(value) && value > 0) return true;
  return false;
};

export const resendEmailVerification = async (): Promise<boolean> => {
  try {
    const request = await fetch('/api/resend-email-verification', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const response = await request.json();
    if (response.ok) return true;
    return false;
  } catch (error) {
    return false;
  }
};

export const sendEmailVerification = async (email: string): Promise<boolean> => {
  try {
    const request = await fetch('/api/send-email-verification', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    const response = await request.json();
    if (response.ok) return true;
    return false;
  } catch (error) {
    return false;
  }
};

export const getDatGames = async (games: string[], type: string) => {
  // const favoritesParam = games.length > 0 ? `favorites=${games.join(',')}` : '';

  try {
    const res = await fetch('/api/casino/games', {
      method: 'POST',
      body: JSON.stringify({ favorites: games, type }),
      cache: 'force-cache',
      next: { revalidate: 60 },
    });
    const game = await res.json();
    return game;
  } catch (error) {
    return [];
  }
};

export const getDataGamesTournaments = async (games: string[], type: string, provider: string) => {
  // const favoritesParam = games.length > 0 ? `favorites=${games.join(',')}` : '';

  try {
    const res = await fetch('/api/casino/games', {
      method: 'POST',
      body: JSON.stringify({ favorites: games, type, provider }),
      cache: 'force-cache',
      next: { revalidate: 60 },
    });
    const game = await res.json();
    return game;
  } catch (error) {
    return [];
  }
};

export const moneyFormat = (amount: string | number) => {
  const data = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));

  return data;
};

export const formatMoneyWithCurrency = (amount: string | number, currency?: string): string => {
  const symbols: Record<string, string> = {
    GTQ: 'Q',
    DOP: 'RD$',
    USD: 'US$',
  };

  const symbol = currency ? (symbols[currency] || 'US$') : 'US$';
  const formattedAmount = moneyFormat(amount);

  return `${symbol} ${formattedAmount}`;
};

export const transactionType = (type: number): string => {
  switch (type) {
    case TransactionTypes.RECHARGE:
      return 'Recarga';
    case TransactionTypes.DEPOSIT:
      return 'Deposito';
    case TransactionTypes.WITHDRAWL:
      return 'Retiro';
    case TransactionTypes.ADJUST_DEP:
      return 'Ajuste de Deposito';
    case TransactionTypes.ADJUST_DISP:
      return 'Ajuste de Disponible';
    case TransactionTypes.RECHARGE_DISP:
      return 'Recarga de Disponible';
    case TransactionTypes.COMMISSION:
      return 'Recompensa';
    default:
      return '';
  }
};

export const gatewayType = (type: number): string => {
  switch (type) {
    case Gateway.MANUAL:
      return 'Manual';
    case Gateway.BANK:
      return 'Banco';
    case Gateway.TIGO:
    case Gateway.CASH:
      return 'Efectivo';
    case Gateway.CHIVO:
      return 'Chivo';
    case Gateway.EUKAPAY:
    case Gateway.TEN_COINS:
      return 'Crypto';
    case Gateway.UNLIMIT:
    case Gateway.PAYPHONE:
    case Gateway.NICO:
      return 'Tarjeta Credito/Debito';
    default:
      return '';
  }
};

export const uploadFileToS3 = async ({ file, name }: { file: any, name: string }): Promise<string> => {
  try {
    const S3_BUCKET = process.env.NEXT_PUBLIC_VOUCHERS_S3_BUCKET ? process.env.NEXT_PUBLIC_VOUCHERS_S3_BUCKET : '';
    const REGION = process.env.NEXT_PUBLIC_VOUCHERS_S3_REGION ? process.env.NEXT_PUBLIC_VOUCHERS_S3_REGION : '';
    const S3_URL = 'https://sivarbetkyc.s3.eu-north-1.amazonaws.com/';
    const S3_FOLDER = 'vouchers/';

    const time = new Date().getTime();
    const uniqueId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const key = `${S3_FOLDER}${time}-${uniqueId}-${name}`;

    const client = new S3Client({
      region: REGION,
      credentials: {
        accessKeyId: process.env.NEXT_PUBLIC_VOUCHERS_S3_ACCESS_KEY_ID ? process.env.NEXT_PUBLIC_VOUCHERS_S3_ACCESS_KEY_ID : '',
        secretAccessKey: process.env.NEXT_PUBLIC_VOUCHERS_S3_ACCESS_KEY ? process.env.NEXT_PUBLIC_VOUCHERS_S3_ACCESS_KEY : '',
      },
    });

    const command = new PutObjectCommand({
      Bucket: S3_BUCKET,
      Key: key,
      Body: file,
      ContentType: file.type,
      ACL: 'public-read',
    });

    const response = await client.send(command);
    console.log('🚀 ~ uploadFileToS3 ~ response:', response);
    console.log('🚀 ~ uploadFileToS3 ~ response:', response.$metadata);
    console.log('🚀 ~ uploadFileToS3 ~ response:', response.$metadata.httpStatusCode);

    if (response.$metadata.httpStatusCode === 200) {
      return `${S3_URL}${key}`;
    }
    return '';
  } catch (error) {
    console.log('error 1473 =>', error);
    return '';
  }
};

export async function updateFavoriteGames(favorites: string[]) {
  try {
    const res = await fetch('/api/casino/games/favorites', {
      method: 'PATCH',
      body: JSON.stringify({ favorites }),
      next: { revalidate: 60 },
    });

    const data = await res.json();
    return data.favoriteGames;
  } catch (error) {
    await exception(error, { route: '/utils/core.ts: updateFavoriteGames', method: 'DB', req: favorites });
    return { success: false };
  }
}

export const voucherStatus = (status: number) => {
  const voucherListTypes = [
    { value: VoucherTypes.ALL, label: 'Todo' },
    { value: VoucherTypes.REQUESTED, label: 'Solicitado' },
    { value: VoucherTypes.APPROVED, label: 'Aprobado' },
    { value: VoucherTypes.REJECTED, label: 'Rechazado' },
    { value: VoucherTypes.ANNULLED, label: 'Anulado' },
  ];

  const value = voucherListTypes.find((item) => { return item.value === status; })?.label;
  return value;
};

export const formatUnits = (unit: string) => {
  switch (unit) {
    case 'days':
      return 'Días';
    case 'hours':
      return 'Horas';
    case 'minutes':
      return 'Minutos';
    default:
      return '';
  }
};

export const pointsFormula = ({ pointsFormulaValue, betValue }: { pointsFormulaValue: any, betValue: number }): number => {
  const points = pointsFormulaValue && pointsFormulaValue.trim() !== '' ? Math.round(eval(pointsFormulaValue)) : Math.round(betValue * 10);
  return points;
};

export const normalizeImageUrl = (url: string | null | undefined): string => {
  if (!url) {
    return '/img/v2/casino/onError.webp';
  }

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (url.startsWith('/')) {
    return url;
  }

  return url;
};

export const getTimeLabel = (target: Date, now: Date) => {
  let diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  diff -= days * (1000 * 60 * 60 * 24);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff -= hours * (1000 * 60 * 60);
  const minutes = Math.floor(diff / (1000 * 60));
  const label = `${days > 0 ? `${days}d ` : ''}${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
  return label;
};

export const formatDisplayName = (name?: string, lastName?: string): { firstName: string, lastName: string } => {
  const toCapitalCase = (str: string): string => {
    if (!str || str.trim() === '') return '';
    const trimmed = str.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  };

  const getFirstToken = (value?: string): string => {
    if (!value || value.trim() === '') return '';
    return value.trim().split(' ')[0] || '';
  };

  const firstName = toCapitalCase(getFirstToken(name));
  const formattedLastName = toCapitalCase(getFirstToken(lastName));

  return {
    firstName,
    lastName: formattedLastName,
  };
};

export function mapGatewayToPaymentType(gateway: number | null | undefined): string {
  if (!gateway) return 'Other';
  const gatewayMap: Record<number, string> = {
    [Gateway.PAYPHONE]: 'Credit_Card',
    [Gateway.UNLIMIT]: 'Credit_Card',
    [Gateway.PRAXIS]: 'Credit_Card',
    [Gateway.EUKAPAY]: 'Crypto',
    [Gateway.GOOGLEPAY]: 'Digital_Wallet',
    [Gateway.APPLEPAY]: 'Digital_Wallet',
    [Gateway.BANK]: 'Bank_Transfer',
    [Gateway.CASH]: 'Cash',
    [Gateway.TIGO]: 'Mobile_Payment',
    [Gateway.SOMPOPO]: 'Mobile_Payment',
    [Gateway.SOMPOPO_APP]: 'Mobile_Payment',
    [Gateway.PAYCASH]: 'Cash',
    [Gateway.MANUAL]: 'Manual',
  };
  return gatewayMap[gateway] || 'Other';
}

// Comentado: Tracking de pagos no se utilizará por ahora en Powerbet
// Si se necesita en el futuro, descomentar y asegurar que:
// 1. El endpoint de notificaciones esté correctamente configurado para Powerbet
// 2. Se use variable de entorno para la URL (no hardcodeada)
// 3. Se manejen los errores apropiadamente
// export async function sendDepositPaymentNotification({
//   user_id, payment_type, first_deposit, transaction_id, value, currency, country,
// }: {
//   user_id: string;
//   payment_type: string;
//   first_deposit: boolean;
//   transaction_id: string;
//   value: number;
//   currency: string;
//   country: string;
// }): Promise<void> {
//   try {
//     const response = await fetch('https://load.t.hondubet.com/payments', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({
//         user_id,
//         payment_type,
//         first_deposit,
//         transaction_id,
//         value,
//         currency,
//         country,
//       }),
//     });
//
//     if (!response.ok) {
//       console.warn('Failed to send deposit payment notification:', response.status, response.statusText);
//     }
//   } catch (error) {
//     console.warn('Error sending deposit payment notification:', error);
//   }
// }

// Comentado: Voluum tracking no es necesario para Powerbet y podría causar problemas
// si no está configurado correctamente (click_id, endpoints, etc.)
// Si se necesita en el futuro, descomentar y asegurar que:
// 1. Los usuarios tengan click_id configurado
// 2. El endpoint de Voluum esté correctamente configurado para Powerbet
// 3. Se manejen los errores apropiadamente
// 4. Se actualice el User-Agent de hondubet/1.0 a powerbet/1.0 si es necesario
// export async function sendVoluumDeposit(user: any, amount: number) {
//   const firstDepositValue = user.profiles?.first_deposit === true ? '0' : '1';
//
//   if (user.click_id && user.click_id !== '') {
//     try {
//       const url = firstDepositValue === '0'
//         ? `https://trandlesprates.com/postback?cid=${user.click_id}&et=ftd&payout=${amount}`
//         : `https://trandlesprates.com/postback?cid=${user.click_id}&et=deposit&payout=${amount}`;
//
//       const response = await axios.get(url, {
//         timeout: 5000,
//         headers: {
//           'User-Agent': 'hondubet/1.0',
//           Accept: '*/*',
//           Connection: 'keep-alive',
//         },
//       });
//
//       console.log('Pixelhere response data:', response.data);
//     } catch (error: any) {
//       if (error.code === 'ECONNABORTED') {
//         console.log('Voluum request timed out');
//       } else {
//         console.log('Error posting to Voluum:', error.message || error);
//       }
//     }
//   }
//
//   return true;
// }
